import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useState, useEffect, useRef} from 'react';
import areEqual from 'react-fast-compare';
import ReactFlow, {
  ControlButton,
  Controls,
  PanOnScrollMode,
  ReactFlowProvider,
  useReactFlow,
} from 'reactflow';

import {makeAnalyticsEventId, trackEventWithModule} from 'analytics/Analytics';
import {assertAndContinue} from 'Asserts';
import {UPDATE_FLOWCHART_MODAL_TYPE} from 'constants/analytics-products/WorkflowAnalyticsConstants';
import {
  ActionMethod,
  ActionOrigin,
  ANALYTICS_MODULE,
  COMMON_ANALYTICS_ACTIONS,
  WORKFLOWS_ANALYTICS_ACTIONS,
} from 'constants/AnalyticsConstants';
import notifications from 'notifications';
import preferences from 'Preferences';
import {FlowchartRenderData} from 'processes/graph-builder/flowchart-viz/FlowchartVizConstants';
import ReactFlowWrapper from 'processes/graph-builder/flowchart-viz/ReactFlowWrapper';
import {REACT_FLOW_PRO_OPTIONS} from 'processes/graph-builder/ReactFlowConstants';
import {PROCESS_FLOWCHART_EDGE_TYPE_TO_STATIC_EDGE_RENDERER} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartStaticEdgeRenderer';
import ProcessUpdateFlowchartModal from 'processes/modals/ProcessUpdateFlowchartModal';
import Tooltip, {TooltipWrapper} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE_LIGHT, GRAY_DARKER, GRAY_LIGHTER, GRAY_SEMIDARK, WHITE} from 'ui-library/Colors';
import usePrevious from 'ui-library/hooks/usePrevious';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import runModal from 'ui-library/runModal';
import {omit} from 'util/omitKey';

import MappingInProgressIndicator from './MappingInProgressIndicator';
import ProcessFlowchartContext from './ProcessFlowchartContext';
import {PROCESS_FLOWCHART_NODE_TYPE_TO_TASK_NODE_RENDERER} from './ProcessFlowchartTaskNodeRenderer';
import {
  DEFAULT_FIT_VIEW_OPTIONS,
  MIN_FLOWCHART_ZOOM,
  MAX_FLOWCHART_ZOOM,
  ProcessFlowchartNodeWidth,
  ProcessFlowchartStaticEdge,
  ProcessFlowchartStaticNode,
  ProcessStaticFlowchart,
} from './ProcessFlowchartVizConstants';
import collapseNodesWithNoTasksInFlowchart from './util/collapseNodesWithNoTasksInFlowchart';
import convertStaticProcessFlowchartForReactFlow from './util/convertStaticProcessFlowchartForReactFlow';
import * as StubbableFunctions from './util/stubbableFunctions';

const lockedProps = {
  nodesConnectable: false,
  nodesDraggable: false,
  nodesFocusable: false,
  edgesFocusable: false,
  connectOnClick: false,
  selectNodesOnDrag: false,
  selectionKeyCode: null,
  zoomOnScroll: false,
  panOnScroll: true,
  panOnScrollMode: PanOnScrollMode.Vertical,
  zoomOnDoubleClick: false,
  panOnDrag: true,
};

function EmptyProcessStaticFlowchartViewer(): JSX.Element {
  return (
    <ReactFlowWrapper className="u-padding" width={ProcessFlowchartNodeWidth.WIDE}>
      <ReactFlow
        {...lockedProps}
        proOptions={REACT_FLOW_PRO_OPTIONS}
        nodes={[]}
        edges={[]}
        nodeTypes={PROCESS_FLOWCHART_NODE_TYPE_TO_TASK_NODE_RENDERER}
      />
    </ReactFlowWrapper>
  );
}

interface ProcessStaticFlowchartControlsProps {
  reactFlowControlsXPosition: number;
}

/**
 * This controls component should be kept in sync with ProcessFlowchartEditorControls, which
 * displays in the flowchart config UI but has different CSS and analytics tracking.
 */
function ProcessStaticFlowchartControls(props: ProcessStaticFlowchartControlsProps): JSX.Element {
  const {zoomIn, zoomOut, fitView} = useReactFlow();

  return (
    <Controls
      className={css(styles.controls)}
      style={{left: -props.reactFlowControlsXPosition}}
      showZoom={false}
      showFitView={false}
      showInteractive={false}
      fitViewOptions={DEFAULT_FIT_VIEW_OPTIONS}
    >
      <Tooltip title="Zoom in">
        <ControlButton
          onClick={() => {
            zoomIn();
          }}
        >
          <BenchlingIcon icon="plus" />
        </ControlButton>
      </Tooltip>
      <Tooltip title="Zoom out">
        <ControlButton
          onClick={() => {
            zoomOut();
          }}
        >
          <BenchlingIcon icon="minus" />
        </ControlButton>
      </Tooltip>
      <Tooltip title="Fit to view">
        <ControlButton
          onClick={() => {
            fitView(DEFAULT_FIT_VIEW_OPTIONS);
          }}
        >
          <BenchlingIcon
            icon="square-corners"
            dataTestAttrs={{
              component: 'ProcessStaticFlowchartControls',
              element: 'autoLayoutButton',
            }}
          />
        </ControlButton>
      </Tooltip>
    </Controls>
  );
}

interface InnerProcessStaticFlowchartViewerBaseProps {
  reactFlowContainerWidth?: number;
  renderData: FlowchartRenderData<ProcessFlowchartStaticNode, ProcessFlowchartStaticEdge>;
  flowchartClientId: string;
  title?: string;
  showNodesWithoutTasks: boolean;
  isFlowchartMappingInProgress?: boolean;
  actionOrigin: ActionOrigin;
  onChangeShowNodesWithoutTasks: (showNodesWithoutTasks: boolean) => void;
}

interface InnerProcessStaticFlowchartEditableProps extends InnerProcessStaticFlowchartViewerBaseProps {
  flowchartProcessId: string;
  rootProcessTaskSchemaId: string;
  sourceType: string;
  onFlowchartModified: () => void;
  editDisabledMessage: string | null;
}

type InnerProcessStaticFlowchartViewerProps =
  | (InnerProcessStaticFlowchartViewerBaseProps & {allowFlowchartModifications?: false})
  | (InnerProcessStaticFlowchartEditableProps & {allowFlowchartModifications: true});

// no need to fitView when the status counts change.
// if you are zoomed into a flowchart and a liveupdate comes in, we shouldn't refit
// the zoom unless a new node is added (probably not even then, but that's harder to solve).
function stripStatusCounts(
  renderData: FlowchartRenderData<ProcessFlowchartStaticNode, ProcessFlowchartStaticEdge>
): FlowchartRenderData<ProcessFlowchartStaticNode, ProcessFlowchartStaticEdge> {
  return {
    nodes: renderData.nodes.map((node) => ({
      ...node,
      data: {
        ...omit(node.data, 'statusCounts'),
      },
    })),
    edges: renderData.edges,
    centersBoundingBox: renderData.centersBoundingBox,
  };
}

function useAutoFitViewOnFlowchartChange(
  renderData: FlowchartRenderData<ProcessFlowchartStaticNode, ProcessFlowchartStaticEdge>
): void {
  const previousRenderData = usePrevious(renderData);

  const {fitView} = useReactFlow();

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // In general, we only enqueue fitView if isDifferentData is true (i.e. the flowchart has changed).
  // But if an autofit has been queued and not yet run, we want to requeue it.
  // Without this, we have the following problem:
  // time 0:
  //    - Width and height of the flowchart are not yet set
  //        - both getNodes and getEdges will return empty arrays
  //    - enqueue fitView via setTimeout
  // time 1:
  //    - width and height are known
  //    - Call fitView << if we are simpling checking isDifferentData, we won't enqueue fitView
  // fitToView runs: nothing happens.
  const hasAutoFitOnce = useRef<boolean>(false);

  useEffect(() => {
    const isDifferentData =
      previousRenderData != null &&
      !areEqual(stripStatusCounts(renderData), stripStatusCounts(previousRenderData));

    if (!hasAutoFitOnce.current || isDifferentData) {
      if (timeoutRef.current != null) {
        clearTimeout(timeoutRef.current);
      }
      if (isDifferentData) {
        hasAutoFitOnce.current = false;
      }
      // We have to defer the call to `fitView` until the next render cycle, after the newly
      // formatted flowchart has been prepared.
      timeoutRef.current = setTimeout(() => {
        hasAutoFitOnce.current = true;
        fitView(DEFAULT_FIT_VIEW_OPTIONS);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderData]);

  useEffect(
    () => () => {
      if (timeoutRef.current != null) {
        clearTimeout(timeoutRef.current);
      }
    },
    []
  );
}

function InnerProcessStaticFlowchartViewer(props: InnerProcessStaticFlowchartViewerProps): JSX.Element {
  const [hideTooltips, setHideTooltips] = useState(false);
  useAutoFitViewOnFlowchartChange(props.renderData);

  const handleMoveStart = useCallback(() => {
    setHideTooltips(true);
  }, []);

  const handleMoveEnd = useCallback(() => {
    setHideTooltips(false);
  }, []);

  const handleDragStart = useCallback(() => {
    setHideTooltips(true);
  }, []);

  const handleDragStop = useCallback(() => {
    setHideTooltips(false);
  }, []);

  if (props.reactFlowContainerWidth == null) {
    return <EmptyProcessStaticFlowchartViewer />;
  }

  return (
    // eslint-disable-next-line react/jsx-no-constructed-context-values -- Disabling for now, but worth fixing
    <ProcessFlowchartContext.Provider value={{hideTooltips, flowchartClientId: props.flowchartClientId}}>
      <ReactFlowWrapper
        width={props.reactFlowContainerWidth}
        containerWidthForControls={props.reactFlowContainerWidth}
      >
        {(reactFlowControlsXPosition) => (
          <div className={css(styles.flowchartWrapper)}>
            <ReactFlow
              nodes={props.renderData.nodes}
              edges={props.renderData.edges}
              fitView={true}
              minZoom={MIN_FLOWCHART_ZOOM}
              maxZoom={MAX_FLOWCHART_ZOOM}
              fitViewOptions={DEFAULT_FIT_VIEW_OPTIONS}
              nodeTypes={PROCESS_FLOWCHART_NODE_TYPE_TO_TASK_NODE_RENDERER}
              edgeTypes={PROCESS_FLOWCHART_EDGE_TYPE_TO_STATIC_EDGE_RENDERER}
              className={css(styles.flowchart)}
              proOptions={REACT_FLOW_PRO_OPTIONS}
              onMoveStart={handleMoveStart}
              onMoveEnd={handleMoveEnd}
              onSelectionDragStart={handleDragStart}
              onSelectionDragStop={handleDragStop}
              {...lockedProps}
            >
              {reactFlowControlsXPosition != null && (
                <ProcessStaticFlowchartControls reactFlowControlsXPosition={reactFlowControlsXPosition} />
              )}
              <div className={css(styles.header)}>
                {props.title && (
                  <div className={css(styles.heading)}>
                    <BenchlingIcon icon="workflow" /> &nbsp;
                    {props.title}
                  </div>
                )}
                <div className={css(styles.toolbar)}>
                  <CheckboxInput
                    id="showNodesWithoutTasksCheckbox"
                    label="Show nodes without tasks"
                    checked={props.showNodesWithoutTasks}
                    className={css(styles.showNodesWithoutTasksCheckbox)}
                    labelStyle={LabelStyle.LABEL}
                    onChange={(e) => {
                      props.onChangeShowNodesWithoutTasks(e.target.checked);
                      const eventGroupId = makeAnalyticsEventId();
                      trackEventWithModule(
                        WORKFLOWS_ANALYTICS_ACTIONS.TOGGLED_SHOW_NODES_WITHOUT_DATA_IN_FLOWCHART_LINEAGE,
                        ANALYTICS_MODULE.WORKFLOWS,
                        {
                          event_group_id: eventGroupId,
                          action_origin: props.actionOrigin,
                          action_method: ActionMethod.BUTTON_CLICK,
                          show_nodes_without_tasks: e.target.checked,
                        }
                      );
                    }}
                    dataTestAttrs={{
                      component: 'ProcessStaticFlowchartViewer',
                      element: 'showNodesWithoutTasksCheckbox',
                    }}
                  />
                  {props.allowFlowchartModifications && (
                    <TooltipWrapper tooltipTitle={props.editDisabledMessage}>
                      <Button
                        buttonStyle={ButtonStyle.TERTIARY_WHITE}
                        size={ButtonSize.MINI}
                        icon="edit"
                        text="Edit"
                        className={css(props.editDisabledMessage == null && styles.editButtonActive)}
                        disabled={props.editDisabledMessage != null}
                        onClick={async () => {
                          const eventGroupId = makeAnalyticsEventId();
                          trackEventWithModule(
                            COMMON_ANALYTICS_ACTIONS.OPENED_MODAL,
                            ANALYTICS_MODULE.WORKFLOWS,
                            {
                              event_group_id: eventGroupId,
                              action_origin: props.actionOrigin,
                              action_method: ActionMethod.POST_CREATION_FLOWCHART_MODIFICATION,
                              task_schema_api_id: props.rootProcessTaskSchemaId,
                              modal_type: UPDATE_FLOWCHART_MODAL_TYPE,
                            }
                          );
                          await runModal(ProcessUpdateFlowchartModal, {
                            flowchartProcessId: props.flowchartProcessId,
                            sourceType: props.sourceType,
                            onFlowchartModified: props.onFlowchartModified,
                            eventGroupId,
                            actionOrigin: props.actionOrigin,
                          });
                        }}
                        dataTestAttrs={{
                          component: 'ProcessStaticFlowchartViewer',
                          element: 'openProcessUpdateFlowchartModal',
                        }}
                      />
                    </TooltipWrapper>
                  )}
                </div>
              </div>
              {props.isFlowchartMappingInProgress && (
                <div className={css(styles.mappingInProgressIndicator)}>
                  <MappingInProgressIndicator
                    text="Mapping in progress..."
                    dataTestAttrs={{
                      component: 'ProcessStaticFlowchartViewer',
                      element: 'mappingInProgressIndicator',
                    }}
                  />
                </div>
              )}
            </ReactFlow>
          </div>
        )}
      </ReactFlowWrapper>
    </ProcessFlowchartContext.Provider>
  );
}

interface StaticFlowchartViewerBaseProps {
  flowchart: ProcessStaticFlowchart;
  isFlowchartMappingInProgress?: boolean;
  actionOrigin?: ActionOrigin;
}

type StaticFlowchartViewerPropsWithoutModification = StaticFlowchartViewerBaseProps & {
  allowFlowchartModifications?: false;
};

type StaticFlowchartViewerPropsWithModification = StaticFlowchartViewerBaseProps & {
  allowFlowchartModifications: true;
  flowchartProcessId: string;
  rootProcessTaskSchemaId: string;
  sourceType: string;
  onFlowchartModified: () => void;
  editDisabledMessage: string | null;
};

export type ProcessStaticFlowchartViewerProps =
  | StaticFlowchartViewerPropsWithoutModification
  | StaticFlowchartViewerPropsWithModification;

const _PROCESSES_SHOW_NODES_WITHOUT_TASKS_IN_FLOWCHART_VISUALIZATION_KEY =
  'PROCESSES_SHOW_NODES_WITHOUT_TASKS_IN_FLOWCHART_VISUALIZATION';

function setShowNodesWithoutTasksPreference(showNodesWithoutTasks: boolean): void {
  preferences.set(_PROCESSES_SHOW_NODES_WITHOUT_TASKS_IN_FLOWCHART_VISUALIZATION_KEY, showNodesWithoutTasks);
}

function getShowNodesWithoutTasksPreference(): boolean {
  const preference = preferences.get(_PROCESSES_SHOW_NODES_WITHOUT_TASKS_IN_FLOWCHART_VISUALIZATION_KEY);
  return preference ?? true;
}

export default function ProcessStaticFlowchartViewer(props: ProcessStaticFlowchartViewerProps): JSX.Element {
  const {ref, width: reactFlowContainerWidth} = StubbableFunctions.useResizeObserver();
  const [showNodesWithoutTasks, setShowNodesWithoutTasks] = useState(getShowNodesWithoutTasksPreference());

  const computeRenderData = (): FlowchartRenderData<
    ProcessFlowchartStaticNode,
    ProcessFlowchartStaticEdge
  > => {
    let flowchart;
    if (showNodesWithoutTasks) {
      flowchart = props.flowchart;
    } else {
      try {
        flowchart = collapseNodesWithNoTasksInFlowchart(props.flowchart);
        // eslint-disable-next-line benchlint/always-throw-from-catch
      } catch (e) {
        assertAndContinue(false, 'Unable to collapse nodes in flowchart', {
          error: e,
          flowchart: props.flowchart,
        });
        notifications.warn(
          'We were unable to remove nodes without tasks from this flowchart. Please reach out to reach out to support@benchling.com for more information.'
        );
        setShowNodesWithoutTasks(true);
        flowchart = props.flowchart;
      }
    }
    return convertStaticProcessFlowchartForReactFlow(flowchart);
  };

  const [renderData, setRenderData] = useState(computeRenderData());
  const prevFlowchart = usePrevious(props.flowchart);
  const prevShowNodesWithoutTasks = usePrevious(showNodesWithoutTasks);

  useEffect(() => {
    if (!areEqual(prevFlowchart, props.flowchart) || prevShowNodesWithoutTasks !== showNodesWithoutTasks) {
      setRenderData(computeRenderData());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.flowchart, showNodesWithoutTasks]);

  const editFlowchartProps = props.allowFlowchartModifications
    ? {
        allowFlowchartModifications: true as const,
        flowchartProcessId: props.flowchartProcessId,
        rootProcessTaskSchemaId: props.rootProcessTaskSchemaId,
        sourceType: props.sourceType,
        onFlowchartModified: props.onFlowchartModified,
        editDisabledMessage: props.editDisabledMessage,
      }
    : {allowFlowchartModifications: false as const};

  return (
    <div ref={ref} className={css(styles.flowchartWrapper)}>
      <ReactFlowProvider>
        <InnerProcessStaticFlowchartViewer
          renderData={renderData}
          reactFlowContainerWidth={reactFlowContainerWidth}
          title={props.flowchart.metadata?.title}
          flowchartClientId={props.flowchart.clientId}
          showNodesWithoutTasks={showNodesWithoutTasks}
          onChangeShowNodesWithoutTasks={(shouldShowNodesWithoutTasks) => {
            setShowNodesWithoutTasksPreference(shouldShowNodesWithoutTasks);
            setShowNodesWithoutTasks(shouldShowNodesWithoutTasks);
          }}
          isFlowchartMappingInProgress={props.isFlowchartMappingInProgress}
          actionOrigin={props.actionOrigin ?? ActionOrigin.UI_UNDETERMINED}
          {...editFlowchartProps}
        />
      </ReactFlowProvider>
    </div>
  );
}

// Z-index for components that should be "on top of" the flowchart viewer
const FLOWCHART_OVERLAY_Z_INDEX = 5;

const styles = StyleSheet.create({
  header: {
    position: 'absolute',
    width: '100%',
    top: 0,
    zIndex: FLOWCHART_OVERLAY_Z_INDEX,
    background: WHITE,
    borderBottomColor: GRAY_LIGHTER,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '48px',
  },
  heading: {
    marginLeft: '16px',
    color: GRAY_DARKER,
  },
  flowchartWrapper: {
    width: '100%',
    height: '100%',
  },
  flowchart: {
    overflow: 'visible',
  },
  controls: {
    position: 'absolute',
    bottom: '20px',
  },
  toolbar: {
    float: 'right',
    height: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    columnGap: '8px',
    marginRight: '16px',
  },
  showNodesWithoutTasksCheckbox: {
    color: GRAY_SEMIDARK,
  },
  editButtonActive: {
    ':hover': {
      color: BLUE_LIGHT,
    },
  },
  mappingInProgressIndicator: {
    position: 'absolute',
    top: 50,
    marginTop: '8px',
    marginLeft: '16px',
    zIndex: FLOWCHART_OVERLAY_Z_INDEX,
  },
});
